import { differenceInCalendarMonths } from "date-fns";
import { useMemo } from "react";
import { useGoalDetailQuery } from "web_myplann/Api/Goals/useGoalDetailQuery";
import { mapGoalDetailDto } from "web_myplann/Goals/Mappers/mapGoalDetailDto";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import type { GoalDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type UseGoalDetailResult = {
	goal: GoalDetail | undefined;
	dto: GoalDetailResponseDTO | undefined;
};

export const useGoalDetail = (id: string): UseGoalDetailResult => {
	const { t } = useResource();
	const numericId = Number(id);
	const { data: goal } = useGoalDetailQuery(numericId, {
		enabled: Number.isFinite(numericId) && numericId > 0,
	});

	const merged = useMemo<GoalDetail | undefined>(() => {
		if (!goal) return undefined;

		const formatDueIn = (targetDate: string | null | undefined): string => {
			if (!targetDate) return "";
			const months = Math.max(
				0,
				differenceInCalendarMonths(new Date(targetDate), new Date()),
			);
			if (months >= 12) {
				return t(Resources.Goals.List.DueIn.Years, {
					count: Math.floor(months / 12),
				});
			}
			return t(Resources.Goals.List.DueIn.Months, { count: months });
		};

		return mapGoalDetailDto({
			id,
			dto: goal,
			dueIn: formatDueIn(goal.targetDate),
		});
	}, [id, goal, t]);

	return { goal: merged, dto: goal };
};
